import { Typography } from "@cpj-ai/panel-npm-package";
import { useGetProfileQuery } from "@graphql/generated";
import { Card } from "@heroui/react";
import SemicircleProgress from "./SemicircleProgress";

type TScoreType = {
  city: number;
  email: number;
  skill: number;
  gender: number;
  mobile: number;
  language: number;
  last_name: number;
  assessment: number;
  first_name: number;
  visa_status: number;
  job_interest: number;
  certification: number;
  date_of_birth: number;
  education_new: number;
  upload_resume: number;
  fluent_language: number;
  native_language: number;
  optional_assessment: number;
  work_experience_new: number;
  education_verification: number;
  work_experience_verification: number;
  course_general: number;
  industry: number;
};

const DashboardScores = () => {
  const { data: profileData } = useGetProfileQuery();
  const parsedScores: TScoreType =
    profileData?.me?.profile?.scores && JSON.parse(profileData?.me?.profile?.scores || "");

  const scores = [
    {
      title: "Education Score:",
      value: (parsedScores?.education_new || 0) + (parsedScores?.education_verification || 0),
    },
    {
      title: "Experience Score:",
      value: (parsedScores?.work_experience_new || 0) + (parsedScores?.work_experience_verification || 0),
    },
    {
      title: "Language Score:",
      value:
        (parsedScores?.language || 0) + (parsedScores?.fluent_language || 0) + (parsedScores?.native_language || 0),
    },
    {
      title: "Certificate Score:",
      value: parsedScores?.certification || 0,
    },
    {
      title: "Skills Score:",
      value: parsedScores?.skill || 0,
    },
    {
      title: "Other Score:",
      value:
        (parsedScores?.assessment || 0) +
        (parsedScores?.optional_assessment || 0) +
        (parsedScores?.first_name || 0) +
        (parsedScores?.last_name || 0) +
        (parsedScores?.mobile || 0) +
        (parsedScores?.city || 0) +
        (parsedScores?.date_of_birth || 0) +
        (parsedScores?.email || 0) +
        (parsedScores?.gender || 0) +
        (parsedScores?.job_interest || 0) +
        (parsedScores?.upload_resume || 0) +
        (parsedScores?.visa_status || 0) +
        (parsedScores?.course_general || 0) +
        (parsedScores?.industry || 0),
    },
  ];

  const TOTAL_POINTS = 500;

  return (
    <Card classNames={{ base: "lg:basis-8/12 xl:basis-9/12 p-5 justify-around" }}>
      <Typography className="font-semibold">Your score is:</Typography>

      <div className="flex flex-col lg:flex-row gap-7 xl:gap-12">
        <div className="flex flex-col lg:flex-row basis-7/12 xl:basis-6/12">
          <div className="basis-5/12 flex lg:flex-col items-center justify-between lg:justify-start ">
            <SemicircleProgress currentPoints={profileData?.me?.profile?.score || 0} totalPoints={TOTAL_POINTS} />
            <div className="flex flex-col items-center">
              <p className="text-xs xl:text-sm font-semibold text-zinc-500">Total score:</p>
              <div className="flex items-end gap-1 mt-2">
                <Typography className="text-primary-500 text-2xl xl:text-2xl font-bold -mb-1">
                  {profileData?.me?.profile?.score || 0}
                </Typography>
                <Typography className="font-bold text-sm text-zinc-500 ">/ {TOTAL_POINTS}</Typography>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:flex lg:flex-col gap-5 border-t-1 border-zinc-400 lg:border-t-0 lg:border-l-1 l basis-7/12 xl:basis-6/12 pt-5 lg:pt-0 lg:ms-1.5 lg:ps-3 xl:ms-7 xl:ps-7">
            {scores.map(({ title, value }) => (
              <div className="flex justify-between" key={title}>
                <Typography className="text-sm text-zinc-500">{title}</Typography>
                <Typography className="text-sm font-medium text-zinc-500">{value}</Typography>
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm basis-5/12 lg:text-[14px] md:text-[12px] text-zinc-500">
          At CPJ, we evaluate your profile through a structured process to help you achieve your career goals. First, we
          verify your identity, education, and work experience. Then, we conduct general and job-specific assessments to
          measure your skills. You’ll receive a score showing how well you match the job requirements. If needed,
          tailored training is offered to improve your skills, and you can retake assessments if necessary. Our free
          training programs and certificates enhance your resume and employability. This process ensures you’re
          perfectly matched with the right opportunities, helping you succeed in your career journey.
        </p>
      </div>
    </Card>
  );
};

export default DashboardScores;
